// START IMPORTS
// Context and configuration imports
import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Hooks imports
import { usePhoneSearchData, useReport } from '@ltvco/refresh-lib/hooks';
import { useMonitorUpsell } from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  unformatPhone,
  isNeighborSpoofed,
  DateUtil,
  usePhoneFormatter,
  isUserInPhoneOnlyTest,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  commonTypes,
  DebugMenu,
  ReportChangesOverview,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useSession,
  useCreateRemouladeReportSnapshot,
  SerializedPerson,
  useMrRogersPhoneLocation,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  OthersAssociatedSection,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  NavLinkDataV2,
  LizanoListItemDataPhone,
  PhoneReportOverview,
  ReportNullState,
  ReportClaimingSection,
  ReportGeneratingScreen,
  MonitoringCardV2,
  useRecentReports,
  ReportActionsWithDateUpdated,
  FraudPhoneThreatDetection,
  CommentsSection,
  usePhoneComments,
  ReportAttachmentsSection,
  ReportAttachmentsCTA,
  AddonUpsellBanner,
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import {
  useFeatureIsOn,
  useFeatureValue,
  useQueryClient,
} from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPhoneNavLinkData,
  getPhoneNavLinkDataV2,
} from 'navLinkData/phoneNavLinkData';
import { isAfterTodaysCutoff } from 'utils/isAfterTodaysCutoff';

import { getHighConfidenceDisplayData } from './utils/getHighConfidenceDisplayData';
// Asset imports

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'phone';
// END CONSTANTS

export function PhoneReport({
  permalink,
  firstReportScreen,
}: PhoneReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const { logError, trackEventGA4 } = useContext(AppConfig);

  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const isInStaggeredPhoneUpdateVariation = useFeatureIsOn('OAR-1352');
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const isInPotentialOwnerVariation = useFeatureValue('RFRSH-3948', '');
  const isInPotentialOwnersVariation1 =
    isInPotentialOwnerVariation === 'RFRSH-3948_variation_1';
  const isInHighConfidenceSectionsExperiment = useFeatureIsOn('RFRSH-4240');

  const { shouldShowMonitoringAddonUpsell } = useMonitorUpsell();
  const [, triggerUpdate] = useState(0);

  const createRemouladeReportSnapshot = useCreateRemouladeReportSnapshot();
  const queryClient = useQueryClient();
  const { phoneOnlyVariation } = useSubscriptionAccessConfigContext();

  const currentUserInfo = account?.account?.user_info;
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  const {
    isFeatureBlockedModalOpen: isMonitoringModalOpen,
    openFeatureBlockedModal: openMonitoringModal,
    closeFeatureBlockedModal: closeMonitoringModal,
  } = useFeatureBlockedModal();
  const [monitoringPaymentModalNumber, setMonitoringPaymentModalNumber] =
    useState<number>(0);

  const openMonitoringUpsellModal = (): void => {
    setMonitoringPaymentModalNumber(0);
    openMonitoringModal();
  };

  const zeroed = isZeroed(queryResult);
  const theme = useTheme();
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const {
    data: phoneSearchData,
    error,
    isError,
  } = usePhoneSearchData(queryResult?.data?.meta?.search_data?.phone_number);

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  const { commentsQuery } = usePhoneComments({
    permalink,
    phoneNumber: rawPhone,
    logError,
    enabled: queryResult.isSuccess,
  });

  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [bbqReportIsReady, setIsBbqReportReady] = useState(false);
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  const [activeMonitor] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [neighborSpoofed, setIsNeighborSpoofed] = useState(false);
  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { data: bbqPhoneData } = useRecentReports(
    { report_type: 'reverse_phone_report' },
    { enabled: bbqReportIsReady }
  );
  const phoneReports = useMemo(
    () => bbqPhoneData?.reports ?? [],
    [bbqPhoneData?.reports]
  );

  const date = new DateUtil();

  const report = useMemo(() => {
    if (!queryResult.isSuccess || !queryResult.data) return null;

    if (queryResult?.data?.entities)
      return ReportFactory.create(queryResult?.data, 'phone');
  }, [queryResult.data, queryResult.isSuccess]);

  const loading = useMemo(() => {
    return queryResult.isLoading || queryResult.isError;
  }, [queryResult.isLoading, queryResult.isError]);

  const mrRogersPhoneLocation = useMrRogersPhoneLocation(report?.phone || '');

  const { phoneOnlyPlanNames } = constants;
  const isPhoneOnlyVariation =
    isUserInPhoneOnlyTest(account, phoneOnlyPlanNames) || phoneOnlyVariation;
  // END DERIVED_DATA_AND_UTILS

  // START COMPUTED_VALUES
  const hidePersonSection =
    isInStaggeredPhoneUpdateVariation &&
    isAfterTodaysCutoff(currentUserInfo?.join_date) &&
    phoneReports.length == 1;
  // END COMPUTED_VALUES

  // START EFFECTS
  useEffect(() => {
    if (isError && error) {
      const msg = error.error || error.message || '';
      if (!msg.toLowerCase().includes('not found')) {
        logError('Error fetching phone search data', error);
      }
    }
  }, [isError, error, logError]);

  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // After a 3 second delay, we expect the report that was just run to be in BBQ
      const timer = setTimeout(() => {
        queryClient.invalidateQueries(['request', 'bbq']);
        setIsBbqReportReady(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [queryResult.data, queryResult.isSuccess]);

  useEffect(() => {
    // Only run the request to remoulade if this report is the user's only phone report, or "first" report
    if (
      !hidePersonSection ||
      phoneReports.length != 1 ||
      report?.data?.people?.length === 0
    )
      return;
    createRemouladeReportSnapshot.mutate({
      permalink,
      update_type: 'phone',
    });
  }, [hidePersonSection, phoneReports]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate)
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    else if (coreUpdateDate)
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );

    if (report) {
      const spoofed = isNeighborSpoofed(
        report?.data,
        account?.account?.user_info.phone_number
      );
      setIsNeighborSpoofed(spoofed);
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="phone"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (loading || queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed || (queryResult.isSuccess && !report)) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={15} />;
  }

  const businesses =
    report?.data?.businesses ??
    report?.data?.rawData?.entities?.businesses ??
    [];
  const businessName = businesses[0]?.names?.[0]?.name ?? '';
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report?.phone;

  const ClaimingComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const MonitoringComponent = (
    <MonitoringCardV2 permalink={permalink} reportType={reportType} />
  );
  const isBusinessSelected =
    reportOptions.potentialOwnerIndex < businesses.length;
  const selectedBusinessName = isBusinessSelected
    ? businesses[reportOptions.potentialOwnerIndex]?.names?.[0]?.name ?? ''
    : '';

  const personOwnerOptions = {
    ...reportOptions,
    potentialOwnerIndex: isBusinessSelected
      ? 0
      : reportOptions.potentialOwnerIndex - businesses.length,
  };
  const owner = report.getOwner(personOwnerOptions);

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const highConfidenceThreshold =
    reportOptions.higherConfidenceThreshold ||
    constants.config.higherConfidenceThreshold;
  const showHighConfidenceDataOnly = reportOptions.showHighConfidenceDataOnly;
  const { phonesForDisplay, emailsForDisplay, addressesForDisplay } =
    getHighConfidenceDisplayData({
      owner,
      showHighConfidenceDataOnly,
      highConfidenceThreshold,
    });
  const phoneItems = isInHighConfidenceSectionsExperiment
    ? phonesForDisplay
    : phones;
  const emailItems = isInHighConfidenceSectionsExperiment
    ? emailsForDisplay
    : emails;
  const addressItems = isInHighConfidenceSectionsExperiment
    ? addressesForDisplay
    : addresses;

  const {
    risk,
    connectivity,
    location: supportingLocation,
  } = report.supportingPhone || {};

  const {
    fraud_score,
    is_risky,
    is_recent_abuse,
    is_spammer,
    is_do_not_call,
    is_voip,
    is_prepaid,
    is_leaked,
  } = risk || {};

  const { active_status, user_activity, is_active } = connectivity || {};

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';

  const otherPhones = phoneItems.filter(
    (phone: commonTypes.Phone) =>
      phone.number !== fallbackPhone && phone.number !== report.phone
  );

  const selectedOwnerDisplayName = isBusinessSelected
    ? selectedBusinessName
    : ownerName || formattedPhoneNumber;

  const intlPhonePiiPolicy =
    account?.account?.subscription_info?.subscription_features
      ?.international_phone_pii_policy;
  const hideInternationalPotentialOwners =
    intlPhonePiiPolicy === 'not_included';
  const hidePotentialOwnersPII = intlPhonePiiPolicy === 'partial_included';

  let potentialOwnersNumber =
    businesses.length + (report.data.people?.length ?? 0);
  if (potentialOwnersNumber === 0 && businessName) potentialOwnersNumber = 1;
  if (isInListV2Variation) potentialOwnersNumber = 1;
  if (hideInternationalPotentialOwners) potentialOwnersNumber = 0;

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume || num_searches || 0;

  let phoneNavLinkDataV2 = getPhoneNavLinkDataV2(
    owner,
    {
      potentialOwnersLength: potentialOwnersNumber,
      searchVolumeLength: searchCount > 0 ? 1 : 0,
      commentsLength: (commentsQuery.data?.comment_count as number) ?? 0,
      neighborSpoofingLength: neighborSpoofed ? 1 : 0,
      spamReportsCount: report.data.spam_reports_count ?? 0,
    },
    theme,
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined
  ) as NavLinkDataV2[];

  let phoneNavLinkData = getPhoneNavLinkData(
    owner,
    {
      potentialOwnersLength: potentialOwnersNumber,
      searchVolumeLength: searchCount > 0 ? 1 : 0,
      commentsLength: (commentsQuery.data?.comment_count as number) ?? 0,
      neighborSpoofingLength: neighborSpoofed ? 1 : 0,
      spamReportsCount: report.data.spam_reports_count ?? 0,
    },
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined
  );

  const comments = commentsQuery.data?.comments ?? report.data.comments ?? [];
  const carrier = report.data.meta?.search_data?.carrier;

  document.title = `${formattedPhoneNumber} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataPhone = {
      permalink,
      phone_number: report?.phone || '',
      type: main_line_type || '',
      carrier: carrier || '',
      area_code_location: mrRogersPhoneLocation[1] || '',
    };

    if (report?.data?.people && report?.data?.people?.length) {
      data.owners = report.data.people.map((owner: SerializedPerson) => {
        return {
          first_name: owner?.identity?.names?.[0]?.parsed?.first || '',
          last_name: owner?.identity?.names?.[0]?.parsed?.last || '',
        };
      });
    }

    return data;
  };

  const people = report.data.people ?? [];
  const potentialOwners = isInPotentialOwnersVariation1
    ? people.slice(0, 1)
    : people;
  const businessNames = businesses
    .map((b) => (b.names?.[0]?.name ? `${b.names[0].name} (Business)` : ''))
    .filter(Boolean);
  const personNames = (potentialOwners ?? []).map((p: SerializedPerson) => {
    const first = p?.identity?.names?.[0]?.parsed?.first ?? '';
    const last = p?.identity?.names?.[0]?.parsed?.last ?? '';
    return `${first} ${last}`.trim() || 'Unknown';
  });
  const potentialOwnersNames = hideInternationalPotentialOwners
    ? []
    : [...businessNames, ...personNames];

  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;

  const internationalPhoneNumber = (rawPhone as string).startsWith('+');
  const supportingPhone = queryResult?.data?.supporting?.phones?.[0];

  const hideOpenReportCTA = internationalPhoneNumber || isPhoneOnlyVariation;

  const fallbackInfo = internationalPhoneNumber
    ? {
        address: supportingPhone?.location?.address || undefined,
        phone: formattedPhoneNumber || undefined,
      }
    : undefined;

  if (internationalPhoneNumber) {
    (phoneNavLinkDataV2 as NavLinkDataV2[]).forEach((item) => {
      if (item?.childItems) {
        item.childItems = item.childItems.filter(
          (child) => child.href !== '#related-reports-section'
        );
      }
    });
  }

  if (hideInternationalPotentialOwners) {
    const potentialOwnersHrefs = new Set([
      '#potential-owners-section',
      '#others-associated-section',
    ]);

    // removes the whole "Potential Owners" group if the international phone PII policy is not_included
    phoneNavLinkDataV2 = phoneNavLinkDataV2.filter(
      (item) =>
        !item.childItems?.some(
          (child) => child.href === '#potential-owners-section'
        )
    );

    phoneNavLinkData = phoneNavLinkData.filter(
      (item) => !potentialOwnersHrefs.has(item.href)
    );
  }
  // END DATA_PREPARATION

  const handleMonitoringUpsellEngagementClick = (
    type: 'banner_phone_report' | 'cta_phone_report'
  ): void => {
    trackEventGA4({
      event_name: 'engagement_click',
      type,
      section_name: 'monitoring_upsell',
    });
    openMonitoringUpsellModal();
  };

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Phone Report"
            headerTitle={formattedPhoneNumber}
            menuItems={
              phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {activeMonitor && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={phoneNavLinkData}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: '#f8f8f8',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />
          {shouldShowMonitoringAddonUpsell && (
            <Box marginTop={1.5}>
              <AddonUpsellBanner
                onUpgradeClick={() =>
                  handleMonitoringUpsellEngagementClick('banner_phone_report')
                }
                bannerMessage="For a one time fee, get unlimited access to alerts on all your reports."
                upsellType="monitoring"
              />
            </Box>
          )}
          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone"
            dateUpdated={reportUpdateDate}
            permalink={permalink}
            navLinkData={phoneNavLinkDataV2 as unknown as NavLinkDataV2[]}
            reportUpgraded={false}
            reportDataForListV2={
              isInListV2Variation ? buildReportDataForListV2() : undefined
            }
            shouldShowMonitoringAddonUpsell={shouldShowMonitoringAddonUpsell}
            onReportMonitorUpsellClick={openMonitoringUpsellModal}
            shouldShowReportAttachments={true}
          />
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}

          <PhoneReportOverview
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            phone={formattedPhoneNumber}
            personName={selectedOwnerDisplayName}
            onReportMonitorUpsellUpgradeClick={() =>
              handleMonitoringUpsellEngagementClick('cta_phone_report')
            }
            neighborSpoofed={neighborSpoofed}
            showPotentialOwnersInOverview={!hideInternationalPotentialOwners}
            phoneOverviewItemsData={{
              comments,
              mainLineType: main_line_type,
              numSearches: searchCount,
              spamScore: spam_score,
              carrier,
              peopleCount: potentialOwnersNumber,
              fraudScore: fraud_score ?? 0,
              isRisky: is_risky,
              isDoNotCall: is_do_not_call ?? false,
              isVOIP: is_voip ?? false,
              isPrepaid: is_prepaid ?? false,
              isValid: report.supportingPhone?.is_valid ?? false,
              isActive: is_active ?? false,
              isLeaked: is_leaked ?? false,
              isSpammer: is_spammer ?? false,
              isRecentAbuse: is_recent_abuse ?? false,
              activeStatus: active_status ?? 'Unknown',
              userActivity: user_activity ?? 'Unknown',
              timezone: supportingLocation?.timezone ?? '',
              potentialOwners: potentialOwnersNames,
              parsedLocation: supportingLocation?.address?.parsed,
              location: supportingLocation?.address?.full ?? '',
            }}
            Monitoring={
              !shouldShowMonitoringAddonUpsell ? MonitoringComponent : undefined
            }
            showHighConfidenceToggle={false}
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({ showHighConfidenceDataOnly: value });
            }}
            useThreatDetectionSection
            internationalPhoneNumber={internationalPhoneNumber}
            potentialOwner={
              hideInternationalPotentialOwners
                ? undefined
                : potentialOwners?.[reportOptions.potentialOwnerIndex]
            }
            fallbackInfo={fallbackInfo}
          />
          {/* END REPORT_OVERVIEW */}

          <ReportAttachmentsCTA
            userCode={currentUserInfo?.user_code || ''}
            permalink={permalink}
            reportType={reportType}
          />

          {ClaimingComponent}

          {/* START PERSONAL_INFO_SECTIONS */}
          <>
            {!hideInternationalPotentialOwners && (
              <>
                <PotentialOwnersV2
                  potentialOwners={potentialOwners || []}
                  potentialOwnerIndex={reportOptions.potentialOwnerIndex}
                  setPotentialOwnerIndex={(index: number) => {
                    handleSetReportOptions({ potentialOwnerIndex: index });
                  }}
                  personName={selectedOwnerDisplayName}
                  permalink={permalink}
                  businesses={businesses}
                  showHighConfidenceDataOnly={
                    reportOptions.showHighConfidenceDataOnly
                  }
                  setShowHighConfidenceDataOnly={(value: boolean) => {
                    handleSetReportOptions({
                      showHighConfidenceDataOnly: value,
                    });
                  }}
                  hidePersonSection={hidePersonSection}
                  reportType={reportType}
                  hideOpenReportCTA={hideOpenReportCTA}
                  fallbackInfo={fallbackInfo}
                  isPhoneOnlyVariation={isPhoneOnlyVariation}
                  hidePII={hidePotentialOwnersPII}
                />
                {isInPotentialOwnersVariation1 &&
                  (() => {
                    const othersAssociated = isBusinessSelected
                      ? people
                      : people.filter(
                          (_: SerializedPerson, i: number) =>
                            i !==
                            reportOptions.potentialOwnerIndex -
                              businesses.length
                        );
                    return othersAssociated.length > 0 ? (
                      <OthersAssociatedSection
                        potentialOwners={othersAssociated}
                        reportTitle={selectedOwnerDisplayName}
                      />
                    ) : null;
                  })()}
                <PhoneSectionV2
                  personName={selectedOwnerDisplayName}
                  phoneList={otherPhones}
                  permalink={permalink}
                  alternateTitle="Other Phone Numbers"
                />
                <EmailSectionV2
                  personName={selectedOwnerDisplayName}
                  emailList={emailItems}
                  permalink={permalink}
                  openEmailReportCTA={!internationalPhoneNumber}
                />
                {!isPhoneOnlyVariation && (
                  <AddressHistorySectionV2
                    addresses={addressItems}
                    personName={selectedOwnerDisplayName}
                    permalink={permalink}
                    realEstate={ownedAssets?.real_estate}
                    alternateTitle={alternativeTitle}
                    openReportCTA={!internationalPhoneNumber}
                    enableCardClick={!internationalPhoneNumber}
                  />
                )}
                <SocialSectionV2
                  personName={selectedOwnerDisplayName}
                  profiles={profiles}
                  usernames={usernames}
                  permalink={permalink}
                  openSocialReportCTA={!internationalPhoneNumber}
                />
                <JobsSectionV2
                  personName={selectedOwnerDisplayName}
                  jobs={jobs}
                  permalink={permalink}
                />
                <EducationSectionV2
                  personName={selectedOwnerDisplayName}
                  educations={educations}
                  permalink={permalink}
                />
                {!isPhoneOnlyVariation && (
                  <AssetsSectionV2
                    personName={selectedOwnerDisplayName}
                    ownedAssets={ownedAssets}
                    permalink={permalink}
                  />
                )}
              </>
            )}
          </>
          {/* END PERSONAL_INFO_SECTIONS */}

          {/* START FRAUD_SCAN_SECTION */}
          <FraudPhoneThreatDetection
            isOnPhoneReport
            fraudScore={fraud_score ?? 0}
            isRisky={is_risky}
            isRecentAbuse={is_recent_abuse}
            isSpammer={is_spammer}
            searchCount={searchCount}
            spamScore={report?.data?.spam_score}
            spamReportsCount={report?.data?.spam_reports_count}
            userComments={comments?.length}
            permalink={permalink}
            spam={spam}
            setSpam={setSpam}
          />
          {/* END FRAUD_SCAN_SECTION */}

          {/* START ADDITIONAL_INFO_SECTIONS */}

          <CommentsSection
            comments={commentsQuery.data?.comments ?? []}
            reportData={report.data}
            permalink={permalink}
            formattedPhoneNumber={formattedPhoneNumber}
            reportType={reportType}
          />

          <ReportAttachmentsSection
            userCode={currentUserInfo?.user_code || ''}
            permalink={permalink}
            reportType={reportType}
          />

          <NotesSectionV2 permalink={permalink} />

          {!internationalPhoneNumber && !hideInternationalPotentialOwners && (
            <RelatedReportsSectionV2
              reportType="phone"
              personName={selectedOwnerDisplayName}
              potentialOwners={people}
              relatives={relatives}
              addresses={addresses}
              phones={phones}
              emails={emails}
              usernames={usernames}
              hidePersonSection={hidePersonSection}
            />
          )}

          <ReportRatingV2
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END ADDITIONAL_INFO_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Phone Report"
        headerTitle={formattedPhoneNumber}
        menuItems={phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
      <FeatureBlockedModal
        isOpen={isMonitoringModalOpen}
        onClose={closeMonitoringModal}
        searchType="Report Monitoring"
        paymentShowModalNumber={monitoringPaymentModalNumber}
        setPaymentShowModalNumber={setMonitoringPaymentModalNumber}
      />
    </Box>
  );
  // END RENDER
}
